import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { agentsApi } from './api'
import type { AgentPayload } from './types'

export function useAgents() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'agents'),
    queryFn: agentsApi.list,
  })
}

export function useCreateAgent() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: AgentPayload) => agentsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'agents') }),
  })
}

export function useUpdateAgent() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AgentPayload }) =>
      agentsApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'agents') }),
  })
}

export function useDeleteAgent() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => agentsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'agents') }),
  })
}

export function useDirectoryMembers(enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'directory-members'),
    queryFn: agentsApi.directoryMembers,
    enabled,
  })
}

export function useAgentTemplates(enabled = true) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'agent-templates'),
    queryFn: agentsApi.listTemplates,
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCloneAgentTemplate() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      agentsApi.cloneTemplate(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'agents') }),
  })
}
